/**
 * "Try it on a sample" strip (S5-T1.4).
 *
 * Removes the biggest tool-first friction ("I don't have a PDF handy") by
 * loading a bundled, fictitious sample document straight into the tool so the
 * visitor sees a live stamp immediately. Samples are same-origin static assets
 * generated by scripts/generate-samples.ts; nothing is uploaded.
 */

import { useState } from "react";

import type { LoadedPdf } from "./DropZone";
import styles from "./SampleStrip.module.css";

const SAMPLES = [
  { slug: "discovery-production", label: "Discovery production" },
  { slug: "medical-records", label: "Medical records" },
  { slug: "foia-response", label: "FOIA response" },
  { slug: "patent-filing", label: "Patent filing" },
  { slug: "insurance-claim", label: "Insurance claim" },
] as const;

interface SampleStripProps {
  onPick: (pdf: LoadedPdf) => void;
  /** True when something is already loaded, so the strip offers a switch rather
   *  than a first try. The strip stays visible either way. */
  hasFile?: boolean;
}

export function SampleStrip({ onPick, hasFile = false }: SampleStripProps) {
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function pick(slug: string, label: string): Promise<void> {
    setError(null);
    setLoadingSlug(slug);
    try {
      const res = await fetch(`/samples/${slug}.pdf`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      onPick({ name: `${label} (sample).pdf`, bytes });
    } catch {
      setError("Couldn't load that sample. Try another, or drop your own PDF.");
    } finally {
      setLoadingSlug(null);
    }
  }

  return (
    <div className={styles.strip}>
      <span className={styles.label}>{hasFile ? "Try a sample:" : "No PDF handy? Try a sample:"}</span>
      <div className={styles.chips}>
        {SAMPLES.map((s) => (
          <button
            key={s.slug}
            type="button"
            className={styles.chip}
            disabled={loadingSlug !== null}
            onClick={() => void pick(s.slug, s.label)}
          >
            {loadingSlug === s.slug ? "Loading…" : s.label}
          </button>
        ))}
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
